import { ChevronLeft, ChevronRight, X } from "lucide-react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";

import {
  ImageLightboxContext,
  type ImageLightboxImage,
} from "./image-lightbox-context";

const LONG_IMAGE_ASPECT_RATIO = 2.4;

interface ImageLightboxProps {
  alt: string;
  canGoNext?: boolean;
  canGoPrevious?: boolean;
  onClose: () => void;
  onNext?: () => void;
  onPrevious?: () => void;
  showNavigation?: boolean;
  src: string;
}

interface ImageLightboxSession {
  currentIndex: number;
  images: ImageLightboxImage[];
  onImageView?: (imageId: string, operationId: string) => void;
  operationId: string;
}

export function ImageLightbox({
  alt,
  canGoNext = false,
  canGoPrevious = false,
  onClose,
  onNext,
  onPrevious,
  showNavigation = false,
  src,
}: ImageLightboxProps) {
  const [imageState, setImageState] = useState<{
    layout: "regular" | "long";
    src: string;
  } | null>(null);
  const imageLayout = imageState?.src === src ? imageState.layout : "loading";

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      } else if (event.key === "ArrowLeft" && canGoPrevious) {
        event.preventDefault();
        onPrevious?.();
      } else if (event.key === "ArrowRight" && canGoNext) {
        event.preventDefault();
        onNext?.();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [canGoNext, canGoPrevious, onClose, onNext, onPrevious]);

  const lightbox = (
    <div
      aria-label={`图片预览：${alt}`}
      aria-modal="true"
      className="fixed inset-0 z-[200] animate-in bg-black/35 duration-150 fade-in-0 supports-backdrop-filter:backdrop-blur-md motion-reduce:animate-none"
      role="dialog"
    >
      <div
        key={src}
        className={`absolute inset-0 flex justify-center p-4 sm:p-8 ${
          imageLayout === "long" ? "items-start overflow-y-auto" : "items-center"
        }`}
        onClick={(event) => {
          if (event.target === event.currentTarget) {
            onClose();
          }
        }}
      >
        <img
          alt={alt}
          className={`max-w-full rounded-xl object-contain ${
            imageLayout === "long" ? "h-auto" : "max-h-full"
          } ${
            imageLayout === "loading"
              ? "opacity-0"
              : "animate-in duration-200 fade-in-0 zoom-in-95 ease-out motion-reduce:animate-none"
          }`}
          src={src}
          onLoad={(event) => {
            const { naturalHeight, naturalWidth } = event.currentTarget;
            setImageState({
              layout:
                naturalWidth > 0 && naturalHeight / naturalWidth >= LONG_IMAGE_ASPECT_RATIO
                  ? "long"
                  : "regular",
              src,
            });
          }}
        />
      </div>
      {showNavigation && (
        <>
          <button
            aria-label="上一张图片"
            className="absolute left-4 top-1/2 z-10 flex size-11 -translate-y-1/2 items-center justify-center rounded-full bg-black/40 text-white/90 transition-colors hover:bg-black/60 disabled:cursor-not-allowed disabled:opacity-30 sm:left-6"
            disabled={!canGoPrevious}
            type="button"
            onClick={onPrevious}
          >
            <ChevronLeft className="size-6" />
          </button>
          <button
            aria-label="下一张图片"
            className="absolute right-4 top-1/2 z-10 flex size-11 -translate-y-1/2 items-center justify-center rounded-full bg-black/40 text-white/90 transition-colors hover:bg-black/60 disabled:cursor-not-allowed disabled:opacity-30 sm:right-6"
            disabled={!canGoNext}
            type="button"
            onClick={onNext}
          >
            <ChevronRight className="size-6" />
          </button>
        </>
      )}
      <button
        aria-label="关闭图片预览"
        className="absolute right-4 top-4 z-10 flex size-10 items-center justify-center rounded-full bg-black/40 text-white/80 transition-colors hover:bg-black/60 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80"
        type="button"
        onClick={onClose}
      >
        <X className="size-5" />
      </button>
    </div>
  );

  if (typeof document === "undefined") {
    return lightbox;
  }

  return createPortal(lightbox, document.body);
}

export function ImageLightboxProvider({ children, resetSignal }: { children: ReactNode; resetSignal?: number }) {
  const [session, setSession] = useState<ImageLightboxSession | null>(null);
  const previousResetSignal = useRef(resetSignal);
  const reportedOperationIdRef = useRef<string | null>(null);

  const openImageLightbox = useCallback(
    (
      images: ImageLightboxImage[],
      initialImageId: string,
      onImageView?: (imageId: string, operationId: string) => void,
    ) => {
      const currentIndex = images.findIndex((image) => image.id === initialImageId);

      if (currentIndex < 0) {
        return;
      }

      setSession({ currentIndex, images, onImageView, operationId: crypto.randomUUID() });
    },
    [],
  );
  const closeImageLightbox = useCallback(() => setSession(null), []);
  const navigate = useCallback((direction: -1 | 1) => {
    setSession((currentSession) => {
      if (!currentSession) {
        return null;
      }

      const nextIndex = currentSession.currentIndex + direction;

      if (!currentSession.images[nextIndex]) {
        return currentSession;
      }

      return {
        ...currentSession,
        currentIndex: nextIndex,
        operationId: crypto.randomUUID(),
      };
    });
  }, []);
  const contextValue = useMemo(
    () => ({ openImageLightbox }),
    [openImageLightbox],
  );
  const currentImage = session?.images[session.currentIndex];

  useLayoutEffect(() => {
    if (previousResetSignal.current !== resetSignal) {
      setSession(null);
      previousResetSignal.current = resetSignal;
    }
  }, [resetSignal]);

  useEffect(() => {
    if (!session || !currentImage || reportedOperationIdRef.current === session.operationId) {
      return;
    }

    reportedOperationIdRef.current = session.operationId;
    session.onImageView?.(currentImage.id, session.operationId);
  }, [currentImage, session]);

  return (
    <ImageLightboxContext.Provider value={contextValue}>
      {children}
      {session && currentImage && (
        <ImageLightbox
          alt={currentImage.alt}
          src={currentImage.src}
          canGoNext={session.currentIndex < session.images.length - 1}
          canGoPrevious={session.currentIndex > 0}
          showNavigation={session.images.length > 1}
          onClose={closeImageLightbox}
          onNext={() => navigate(1)}
          onPrevious={() => navigate(-1)}
        />
      )}
    </ImageLightboxContext.Provider>
  );
}
